import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Send, CheckCircle2, XCircle, Headset, User } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { open: "bg-amber-soft text-amber-warm", in_progress: "bg-blue-50 text-blue-600", resolved: "bg-brand-soft text-brand", closed: "bg-slate-100 text-slate-500" };

export default function ChatThread({ ticketId, mode }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const url = mode === "agent" ? `/agent/tickets/${ticketId}` : `/support/tickets/${ticketId}`;

  const { data: ticket } = useQuery({ queryKey: ["chat", ticketId], queryFn: () => api.get(url).then((r) => r.data), refetchInterval: 4000, enabled: !!ticketId });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [ticket?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    try {
      await api.post(`${url}/messages`, { text: t });
      qc.invalidateQueries({ queryKey: ["chat", ticketId] });
      qc.invalidateQueries({ queryKey: ["agent-tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (s) => {
    try { await api.patch(url, { status: s }); qc.invalidateQueries({ queryKey: ["chat", ticketId] }); qc.invalidateQueries({ queryKey: ["agent-tickets"] }); toast.success(`Marked ${s.replace(/_/g, " ")}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const mine = (m) => (mode === "agent" ? m.from === "agent" : m.from === "customer");
  if (!ticket) return <div className="flex h-full items-center justify-center text-sm text-slate-400">Loading conversation…</div>;

  return (
    <div className="flex h-full flex-col" data-testid="chat-thread">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 p-4">
        <div className="min-w-0">
          <p className="truncate font-head font-bold text-slate-900">{ticket.subject}</p>
          <p className="text-xs capitalize text-slate-400">
            {mode === "agent" ? `${ticket.customer_name} · ` : ""}{ticket.category?.replace(/_/g, " ")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${STATUS_COLOR[ticket.status] || "bg-slate-100"}`}>{ticket.status?.replace(/_/g, " ")}</span>
          {mode === "agent" && ticket.status !== "resolved" && <button onClick={() => setStatus("resolved")} data-testid="agent-resolve-btn" className="rounded-lg border border-brand/30 px-2 py-1 text-xs font-semibold text-brand"><CheckCircle2 className="h-3.5 w-3.5" /></button>}
          {mode === "agent" && ticket.status !== "closed" && <button onClick={() => setStatus("closed")} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-500"><XCircle className="h-3.5 w-3.5" /></button>}
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-auto bg-slate-50/60 p-4">
        {(ticket.messages || []).map((m, i) => {
          const own = mine(m);
          return (
            <div key={i} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid="chat-message">
              <div className={`flex max-w-[80%] items-end gap-2 ${own ? "flex-row-reverse" : ""}`}>
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.from === "agent" ? "bg-blue-100 text-blue-600" : "bg-brand-soft text-brand"}`}>
                  {m.from === "agent" ? <Headset className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                </div>
                <div className={`rounded-2xl px-3 py-2 text-sm shadow-sm ${own ? "rounded-br-sm bg-brand text-white" : "rounded-bl-sm border border-slate-200 bg-white text-slate-800"}`}>
                  {m.from === "agent" && m.agent_name && <p className="mb-0.5 text-[10px] font-bold opacity-70">{m.agent_name}</p>}
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <p className={`mt-1 text-[10px] ${own ? "text-white/70" : "text-slate-400"}`}>{new Date(m.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2 border-t border-slate-100 p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="chat-input" placeholder="Type a message…"
          className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
        <button onClick={send} data-testid="chat-send-btn" className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand text-white hover:bg-brand-deep active:scale-95"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
